import { Component, OnInit, Injector, HostListener, OnDestroy, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { WorkOrdersService } from '../../work-orders.service';
import { GAWorkOrderService } from '../../../ve.service';
import { CgpConfirmationDialogService } from '../../../../../shared/cgp-confirmation-dialog/cgp-confirmation-dialog.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { GaNotificationDialogComponent } from '../dialogs/ga-notification-dialog/ga-notification-dialog.component';
import { GaMainFormComponent } from '../forms/ga-main-form/ga-main-form.component';
import { FileUploadService } from '../../work-order-file-upload/file-upload.service';
import { WoAssignFormComponent } from '../../forms/wo-assign-form/wo-assign-form.component';
import { AuthenticationService } from '../../../../../auth.service';

@Component({
  selector: 'ga-wo-edit',
  templateUrl: './ga-wo-edit.component.html',
  styleUrls: ['../../work-orders.component.scss']
})
export class GaWoEditComponent implements OnInit, OnDestroy {
  appCardTitle: string;
  router: Router;
  service: WorkOrdersService;
  gaWorkOrderService: GAWorkOrderService;
  cgpConfirmationDialogService: CgpConfirmationDialogService;
  authService: AuthenticationService;
  fileUploadService: FileUploadService;
  isPrint = false;
  isCancel = false;
  matDialog: MatDialog;

  @ViewChild('workOrderDetailsRef') private readonly workOrderDetailsRef: GaMainFormComponent;
  @ViewChild('addInfoRef') private readonly addInfoRef: GaMainFormComponent;
  @ViewChild('deliverablesObjRef') private readonly deliverablesObjRef: GaMainFormComponent;
  @ViewChild('workOrderCommentsRef') private readonly workOrderCommentsRef: GaMainFormComponent;

  constructor(injector: Injector) {
    this.service = injector.get(WorkOrdersService);
    this.gaWorkOrderService = injector.get(GAWorkOrderService);
    this.router = injector.get(Router);
    this.cgpConfirmationDialogService = injector.get(CgpConfirmationDialogService);
    this.matDialog = injector.get(MatDialog);
    this.fileUploadService = injector.get(FileUploadService);
    this.authService = injector.get(AuthenticationService);
  }

  ngOnInit() {
    console.log('ngOnInit');
    const workOrder = this.getGAWorkOrder();
    this.setPageTitle(workOrder.WorkOrderNumber);
  }

  ngOnDestroy() {
    this.service.resetForm();
    this.service.enableForm();
    this.service.resetGridView();
  }

  setPageTitle(workOrderNumber: any) {
    let action;
    if (this.service.isViewOnlyWO) {
      action = '';
    } else if (this.service.isCopyWO) {
      action = 'Copy ';
    } else {
      action = 'Edit ';
    }
    this.appCardTitle = `${action}GA Work Order: ` + workOrderNumber;
  }

  onTabSelect(event) {
    if (event.index === 1) { this.fileUploadService.fileType = 'Instructions' }
    else if (event.index === 2) { this.fileUploadService.fileType = 'AditionalInfo' }
    this.fileUploadService.getFiles();
  }

  print() {
    //   Check to see if user selected Print work order
    //   If so, open print dialog and then route back to workorders
    const workOrder = this.getGAWorkOrder();
    const wordOrderDetails = this.workOrderDetailsRef.getPrintElement();
    const addInfo = this.addInfoRef.getPrintElement();
    const deliverablesObj = this.deliverablesObjRef.getPrintElement();
    const workOrderComments = this.workOrderCommentsRef.getPrintElement();
    const printWindow = window.open('', '_blank', 'top=0,left=0,height=100%,width=auto');
    printWindow.document.open();
    printWindow.document.write(`<html>
                                    <head>
                                        <style>
                                          .row {
                                            display: flex;
                                            flex-direction: row;
                                            justify-content: space-between;
                                            width: 90%;
                                            margin: 0 auto;
                                          }
                                          .col {
                                            display: flex;
                                            flex-direction: column;
                                            justify-content: space-between;
                                            width: 25%;
                                          }
                                          .group {
                                            position: relative;
                                            margin-bottom: 10px;
                                            display: inline-flex;
                                            justify-content: space-between;
                                          }
                                        </style>
                                        <title>GA Work Order: ${workOrder.WorkOrderNumber}</title>
                                    </head>
                                    <body onload="window.print()">
                                            <header>
                                                <h1>GA Work Order: ${workOrder.WorkOrderNumber}</h1>
                                            </header>
                                            ${wordOrderDetails.nativeElement.innerHTML}
                                            ${addInfo.nativeElement.innerHTML}
                                            ${deliverablesObj.nativeElement.innerHTML}
                                            ${workOrderComments.nativeElement.innerHTML}
                                    </body>
                                </html>`);
    printWindow.document.close();
  }

  saveOrSubmit(event: any) {
    const action = event.currentTarget.id;
    const workOrder = this.getGAWorkOrder();
    let draftStatusId: any;

    this.gaWorkOrderService.read('/GetWorkOrderStatuses')
      .then(response => {
        response.forEach(element => {
          if (element.Description.toLowerCase() === 'draft') {
            draftStatusId = element.Id;
          }
        });

        if (action.toLowerCase() === 'save' && workOrder.StatusId !== draftStatusId) {
          this.notifyUsers(workOrder);
        } else {
          this.service.saveOrSubmit(action, workOrder);
        }
      });
  }

  back = () => {
    const workOrder = this.getGAWorkOrder();
    if (this.service.isViewOnlyWO) {
      this.router.navigate([this.service.workOrderGridRouting[this.service.getWorkOrderGridType()]()]);
    } else {
      this.service.back(workOrder, false);
    }
  }

  cancel() {
    const workOrder = this.getGAWorkOrder();
    this.service.back(workOrder, true);
  }

  /**
   * Build GA Work Order object to send in request
   * payload for either Submit or Save actions
   */
  getGAWorkOrder(): any {
    const workOrder = this.service.workOrderDetailsForm.value;
    workOrder.AddInfoCategories = workOrder.AddInfoCategories
      ? workOrder.AddInfoCategories
      : [];
    workOrder.ToolingOperatorInterfaces = workOrder.ToolingOperatorInterfaces
      ? workOrder.ToolingOperatorInterfaces
      : [];
    delete workOrder.ProgramDetailId;
    return workOrder;
  }

  public get isViewOnly(): boolean {
    return this.service.isViewOnlyWO;
  }

  public get showSubmit(): boolean {
    return this.service.isCopyWO || this.service.isNewWO
      || this.service.woStatus.toLowerCase() === 'draft';
  }

  public isFormValid = (): boolean => {
    const isWoDetailsValid = this.service.workOrderDetailsForm.valid;
    const isAddInfoValid = Boolean(this.service.additionalInfoForm.value.Id) ?
      this.service.isAddInfoFormValid.getValue() && this.service.additionalInfoForm.valid :
      this.service.isAddInfoFormValid.getValue();
    const isDelObjValid = this.service.isDeliverablesObjFormValid && this.service.deliverablesObjectivesForm.valid;
    return isWoDetailsValid && isAddInfoValid && isDelObjValid;
  }

  /**
   * Determines if a user can assign the work order to an engineer.
   *
   * @readonly
   * @type {boolean}
   * @memberof BodyWoEditComponent
   */
  public get showAssign(): boolean {
    const user = this.authService.getUser();
    const isUnAssigned = this.service.woStatus === 'UnAssigned';
    const isAdmin = this.isUserInRole(user, 'Administrator');
    const isVeLead = this.isUserInRole(user, 'Lead');
    return isUnAssigned && (isAdmin || isVeLead);
  }

  /**
  * Assigns the current work order to an engineer.
  * VE Admins and Leads can assign work orders from this if the work order is in un-assigned status.
  *
  * @memberof BodyWoEditComponent
  */
  AssignNewWorkOrder() {
    const dialog = WoAssignFormComponent;
    const data = {
      title: `Schedule Work Order`,
      yesLabel: 'Submit',
      noLabel: 'Cancel',
      requiredDate: this.service.workOrderDetailsForm.get('RequiredDate').value,
      workOrderId: this.service.workOrderDetailsForm.get('Id').value,
    };

    const dialogRef = this.matDialog.open(dialog, { data });

    dialogRef.afterClosed()
      .subscribe(response => {
        if (response) {
          this.router.navigate([this.service.workOrderGridRouting[this.service.getWorkOrderGridType()]()]);
        }
      });
  }

  /**
   * Determines if the user is only a VE_User.
   *
   * @param {*} user
   * @param {*} role
   * @returns
   * @memberof DataGridComponent
   */
  isUserInRole(user, role) {
    const isVERole = user.RoleClaims.filter(roleClaim => {
      const userRoleClaimsContents = roleClaim.split('_');
      const userRoleClaimsModule = userRoleClaimsContents[0];
      const userRoleClaimsRole = userRoleClaimsContents[2];
      const currentUserModule = this.authService.getModule().split('_');
      if (userRoleClaimsRole && userRoleClaimsRole.includes(role) && currentUserModule[1].toLowerCase() === userRoleClaimsModule.toLowerCase()) {
        return roleClaim;
      }
    });
    return isVERole.length !== 0;
  }

  notifyUsers(workOrder: any) {
    const data = {
      title: 'Notify Contacts of Changes',
      yesLabel: 'Submit',
      noLabel: 'Cancel',
      contactsDescription: 'Contacts to Notify:',
      messageDescription: 'Message:',
      statusId: workOrder.StatusId,
    };

    const dialogRef = this.matDialog.open(GaNotificationDialogComponent, { data });

    dialogRef.afterClosed()
      .subscribe(result => {
        if (result) {
          this.gaWorkOrderService.loading.next(true);
          if (!result.NotificationList) {
            result.NotificationList = [];
          }
          if (!result.EmailNotes) {
            result.EmailNotes = '';
          }
          const body = { ...workOrder, ...result };

          return this.gaWorkOrderService.save(body, false, '/Save?workOrderId=' + workOrder.Id)
            .then(response => {
              this.gaWorkOrderService.loading.next(false);
              this.service.displaySnackbar(response, 'save');
            });
        }
      });
  }
}
